import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import ReactDiffViewer from "react-diff-viewer-continued";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from 'axios'; // Import axios
import { debounce } from "lodash";
import { addAdditionalTemplateHeaderDetails } from "../../utils/rctreeDataConversion";
import { api } from "../../redux/api";

export default function SaveDiffViewer({ whatChangedOpen, setWhatChangedOpen, templateInfo }) {
  const { t } = useTranslation();
  const { account, baseUrl, user, token } = useSelector((state) => state.queryParams);
  const [oldTemplateData, setOldTemplateData] = useState(false);
  const [newTemplateData, setNewTemplateData] = useState(false);
  const [viewType, setViewType] = useState('SBS');
  const [loading, setLoading] = useState(false);
  const [diffRows, setDiffRows] = useState([]);
  const [diffPoll, setDiffPoll] = useState(0);
  const splitPath = templateInfo.path.split('/');
  const extendedPath = `${splitPath[0]} / ${splitPath[1]} / ${typeTranslation[templateInfo.template_type]} / ${splitPath[2]}.json`;
  const containerRef = useRef();
  const [htmlContent, setHtmlContent] = useState(null);

  const handleModalClose = () => setWhatChangedOpen(false);
  const handleRadioChange = (e) => setViewType(e.target.value);

  const oldTemplateHeader = oldTemplateData?.TEMPLATE?.TEMPLATE_HEADER;
  const newTemplateHeader = newTemplateData?.TEMPLATE?.TEMPLATE_HEADER;

  const fetchDiffData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      const requestData = {
        account,
        user: templateInfo.user,
        workflow_group_name: templateInfo.workflow_group,
        workflow_name: templateInfo.template,
        template_name: templateInfo.template,
        is_server: templateInfo.is_server,
      };

      const urlMap = {
        workflow: `${baseUrl}/workbench?workflow_group_name=${requestData.workflow_group_name}&user=${user}&action=get_workflow_diff&account=${requestData.account}&workflow_name=${requestData.workflow_name}&html_only=true`,
        outbound: `${baseUrl}/workbench?action=get_outbound_diff&user=${user}&account=${requestData.account}&workflow_group_name=${requestData.workflow_group_name}&template_name=${requestData.template_name}`,
        inbound: `${baseUrl}/workbench?action=get_inbound_diff&user=${user}&account=${requestData.account}&workflow_group_name=${requestData.workflow_group_name}&template_name=${requestData.template_name}`,
      };

      const url = urlMap[templateInfo?.template_type];
      if (url) {
        response = await axios(url, {
          responseType: "text",
          headers: { "Authorization": `Bearer ${token}` },
        });
        setLoading(false);
        setHtmlContent(response.data);
      } else {
        throw new Error("Invalid template type");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      setHtmlContent(error.response?.data || "Error fetching data");
    } finally {
      setLoading(false);
    }
  }, [templateInfo, account, user, baseUrl]);

  useEffect(() => {
    if (templateInfo) {
      fetchDiffData();
    }
  }, [templateInfo, fetchDiffData]);

  const getDiffRows = () => {
    if (diffPoll > 20) {
      setDiffPoll(0);
      return false;
    } else {
      setDiffPoll(diffPoll + 1);
    }

    const tableRows = document.querySelectorAll(
      "#local-revision-diff-container>table>tbody>tr"
    );
    const diffRows = [];

    let previousValue = 0;
    let lastDiffRow = 0;
    let currRow = 0;

    const getOffset = (el) => {
      return {
        top: el.offsetTop,
        left: el.offsetLeft
      };
    }

    for (let row of tableRows) {
      currRow++;
      for (let rowChild of row.children) {
        if (
          rowChild.className.includes("diff-added") ||
          rowChild.className.includes("diff-removed")
        ) {
          const currentValue = getOffset(row).top;
          if (diffRows.length === 0) {
            diffRows.push({ previousValue: currentValue, currentValue });
            previousValue = currentValue;
            lastDiffRow = currRow;
          } else {
            if (currRow - lastDiffRow > 1) {
              diffRows[diffRows.length - 1]["nextValue"] = currentValue;
              diffRows.push({ previousValue, currentValue });
              previousValue = currentValue;
            } else {
              diffRows.push({ previousValue, currentValue });
            }
            lastDiffRow = currRow;
          }
          break;
        }
      }
    }
    setDiffRows(diffRows);
  }

  const handlePrevNext = (next) => {
    if (!diffRows.length) return;

    const container = document.querySelector(".compare-local-revisions_body");
    const position = container.scrollTop;

    if (next) {
      for (let i = 0; i < diffRows.length; i++) {
        const difference = diffRows[i].currentValue - position;
        if (difference >= 0) {
          const nextValue = diffRows[i]?.nextValue;
          if (nextValue) {
            container.scrollTo({ top: nextValue, behavior: "smooth" });
            return;
          }
        }
      }
      container.scrollTo({ top: diffRows[0].currentValue, behavior: "smooth" });
    } else {
      for (let i = 0; i < diffRows.length; i++) {
        const difference = position - diffRows[i].currentValue;
        if (difference <= 0) {
          const previousValue = diffRows[i].previousValue;
          container.scrollTo({ top: previousValue, behavior: "smooth" });
          return;
        }
      }
      container.scrollTo({ top: diffRows[diffRows.length - 1].currentValue, behavior: "smooth" });
    }
  }


  const debounceHandler = useCallback(debounce(getDiffRows, 200), []);


  useEffect(() => {
    if (newTemplateData) getDiffRows();
  }, [viewType]);

  useEffect(() => {
  }, [newTemplateData]);

  useEffect(() => {
    if (!diffRows.length) {
      setTimeout(() => {
        getDiffRows();
      }, 500);
    }
  }, [diffRows]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(debounceHandler);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef.current]);

  return (
    <Dialog
      ref={containerRef}
      open={whatChangedOpen}
      onClose={handleModalClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth={"xl"}
      fullWidth
    >
      <DialogTitle>
        <div className="compare-local-revision_header dialog-header">
          <h1>{extendedPath}</h1>
        </div>
      </DialogTitle>
      <DialogContent className="compare-local-revisions_body hei
      ght-100" dividers>
        <div id="local-revision-diff-container" style={{ height: '100%' }}>
          {htmlContent ? (
            <iframe
              srcDoc={htmlContent}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="HTML Content Viewer"
            ></iframe>
          ) : loading ? (
            <div className='loading-overlay' style={{ marginTop: "15%", display: "grid", gap: "20px" }}>
              <CircularProgress size={"4rem"} />
              <div>{t('LOADING_IND')}</div>
            </div>
          ) : (
            ""
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          onClick={handleModalClose}
          variant={"outlined"}
        >
          {t('BTN_RETURN_TO_SAVE_DIALOG')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

//folder names, per type
const typeTranslation = {
  inbound: 'inbound_templates',
  outbound: 'outbound_templates',
  workflow: 'workflows',
}

// Customized Styling Object for Diff Container
const customizedStyle = {
  variables: {
    light: {
      diffViewerBackground: '#fff',
      diffViewerColor: '#212529',
      addedBackground: '#e6ffed',
      addedColor: '#24292e',
      removedBackground: '#ffeef0',
      removedColor: '#24292e',
      wordAddedBackground: '#acf2bd',
      wordRemovedBackground: '#fdb8c0',
      addedGutterBackground: '#cdffd8',
      removedGutterBackground: '#ffdce0',
      gutterBackground: '#f7f7f7',
      gutterBackgroundDark: '#f3f1f1',
      highlightBackground: '#fffbdd',
      highlightGutterBackground: '#fff5b1',
      codeFoldGutterBackground: '#dbedff',
      codeFoldBackground: '#f1f8ff',
      emptyLineBackground: '#fafbfc',
      gutterColor: '#212529',
      addedGutterColor: '#212529',
      removedGutterColor: '#212529',
      codeFoldContentColor: '#212529',
      diffViewerTitleBackground: '#fafbfc',
      diffViewerTitleColor: '#212529',
      diffViewerTitleBorderColor: '#eee',
    }
  },
  diffContainer: {
    border: '1px solid #BBBBCC',
    fontSize: 'medium',
    fontWeight: '500',
    width: "100% !important",
    wordBreak: "break-all"
  },
  diffRemoved: { backgroundColor: '#E99696' },
  diffAdded: { backgroundColor: '#99EE99' },
  lineNumber: { backgroundColor: '#EEEEDD' },
  gutter: { backgroundColor: '#EEEEDD !important', border: '1px solid #BBBBCC', color: '#888866' },
}
